import React, { useEffect, useState } from 'react';
import { useArtworksContext } from '../hooks/useArtworksContext';
import { useAuthContext } from '../hooks/useAuthContext';
import { axiosInstance } from '../context/AuthContext';
import { useParams, Link } from 'react-router-dom';
import EmotionCountBarChart from '../components/EmotionCountBarChart';
import EmotionRadialChart from '../components/EmotionRadialChart';
import EmotionPieChart from '../components/EmotionPieChart';
import EmotionScatterPlot from '../components/EmotionScatterplot';
import EmotionCandlestick from '../components/EmotionCandlestick';
import BodyLanguageDistributionChart from '../components/BodyLanguageDistributionChart';
import ArtworkSubSectionDetails from '../components/ArtworkSubSectionDetails';
import ArtworkDetailsSkeleton from '../components/ArtworkDetailsSkeleton';
import ChartSkeleton from '../components/ChartSkeleton';
import Navbar from '../components/Navbar';
import AOS from 'aos';
import CameraDetectionChart from '../components/CameraDetectionChart';
import Toast from '../components/Toast';

function ArtworkPage() {
    const { id } = useParams()
    const [artwork, setArtwork] = useState({})
    const [artworkLoading, setArtworkLoading] = useState(true)
    const [showBlackOverlay, setShowBlackOverlay] = useState(false)
    const [showToast, setShowToast] = useState(false)
    const [toastMessage, setToastMessage] = useState('')
   

    useEffect(() => {
        AOS.init({
          duration: 1000,
          offset: 100,
        });
    }, []);

    useEffect(() => {
        const fadeInTimer = setTimeout(() => {
            setShowBlackOverlay(true);
        }, 300);
        
        return () => clearTimeout(fadeInTimer);
    }, [id]);

    const {gallery, dispatch} = useArtworksContext()
    const {user} = useAuthContext()

    useEffect(() => {
        setArtworkLoading(true);
        let found = false;
        
        if (gallery && gallery.length > 0) {
            for (const oneArtwork of gallery) {
                if (oneArtwork._id === id) {
                    setArtwork(oneArtwork)
                    setArtworkLoading(false);
                    found = true;
                    // Debug log
                    console.log('Artwork from gallery:', oneArtwork);
                    break;
                }
            }
            // If artwork not found in gallery after checking all items
            if (!found) {
                setArtworkLoading(false);
            }
        } else {
            const fetchArtworks = async () => {
                try {
                    const response = await axiosInstance.get(`/artworks/${id}`);
                    setArtwork(response.data);
                    setArtworkLoading(false);
                    // Debug log
                    console.log('Artwork from fetch:', response.data);
                } catch (error) {
                    console.error('Error fetching artwork:', error);
                    setArtworkLoading(false);
                }
            };

            fetchArtworks();
        }
    }, [id, gallery, user])

    // Show toast if hasSessions is false or devices is empty
    useEffect(() => {
        if (!artworkLoading && artwork._id) {
            const hasSessions = artwork.hasSessions;
            const devices = artwork.devices || [];
            
            if (hasSessions === false || devices.length === 0) {
                let message = '';
                if (hasSessions === false && devices.length === 0) {
                    message = 'No devices configured for this artwork.';
                } else if (hasSessions === false) {
                    message = 'No session data available for this artwork.';
                } else if (devices.length === 0) {
                    message = 'No devices configured for this artwork.';
                }
                
                setToastMessage(message);
                setShowToast(true);
            }
        }
    }, [artwork, artworkLoading])

    const isLoading = artworkLoading || !artwork._id;

    const getFadeInAttributes = (index = 0) => ({
        'data-aos': 'fade-up',
        'data-aos-delay': index * 120,
        'data-aos-duration': 800,
        'data-aos-anchor-placement': 'top-bottom',
    });

    return (
        <>
            <div 
                className={`fixed inset-0 bg-[#020c24] z-30 pointer-events-none ${
                    showBlackOverlay ? 'opacity-90' : 'opacity-20'
                }`}
                aria-hidden="true"
                style={{
                    transition: 'opacity 3.5s cubic-bezier(0.4, 0, 0.2, 1)'
                }}
            />
            

            
            <div className="relative z-50 min-h-screen overflow-x-hidden">
                <Navbar />
                {!isLoading && artwork._id && (
                    <div className="flex justify-end px-4 md:px-10 pt-4 md:ml-20">
                        
                    </div>
                )}
                <div className={`flex justify-center mt-2 sm:mt-4 pb-8 sm:pb-10 h-full w-full min-w-0 ${
                    showBlackOverlay ? 'opacity-90' : 'opacity-0'
                }`} style={{
                    transition: 'opacity 3.5s cubic-bezier(0.4, 0, 0.2, 1)'
                }}>
                    <div className="flex flex-col justify-center w-full min-w-0 max-w-full rounded px-3 sm:px-4 md:flex-row md:space-x-0 ml-0 md:ml-20">
                        <div className="flex flex-col h-auto w-full">
                            {!isLoading && artwork._id && (
                                <div className="w-full text-center mb-4">
                                    <h2 className="text-2xl font-bold mb-4 text-white">{artwork.title}</h2>
                                    <p className="text-gray-400">{artwork.desc}</p>
                                </div>
                            )}
                            <div className="w-full min-w-0 mx-auto grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-6" data-aos="fade-down">
                                {isLoading ? (
                                    <>
                                        {Array.from({ length: 6 }).map((_, idx) => (
                                            <div key={`skeleton-${idx}`} className="min-w-0" {...getFadeInAttributes(idx)}>
                                                <ChartSkeleton />
                                            </div>
                                        ))}
                                    </>
                                ) : (
                                    <>
                                        {[
                                            { key: 'subsection', 
                                                node: <div className="flex flex-col justify-center items-center gap-2 h-full">
                                                        <ArtworkSubSectionDetails artwork={artwork} /><Link
                                                            to={`/artozphere/artwork/${artwork.slug || artwork._id}`}
                                                            className="inline-flex items-center gap-2 rounded-lg bg-green-600 px-5 py-2.5 text-sm font-semibold text-white shadow hover:bg-green-700 transition-colors"
                                                        >
                                                            Buy this artwork
                                                        </Link>
                                                    </div> 
                                            },
                                            ...(artwork.hasSessions ? [
                                                { key: 'pie', node: <EmotionPieChart artworkId={id} /> },
                                                { key: 'candle', node: <EmotionCandlestick artworkId={id} /> },
                                                { key: 'radial', node: <EmotionRadialChart artworkId={id} /> },
                                                { key: 'body', node: <BodyLanguageDistributionChart artworkId={id} /> },
                                                { key: 'camera', node: <CameraDetectionChart artworkId={id} /> },
                                            ] : []),
                                        ].map(({ key, node }, idx) => (
                                            <div key={key} className="min-w-0" {...getFadeInAttributes(idx)}>
                                                {node}
                                            </div>
                                        ))}
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <Toast 
                message={toastMessage}
                type="warning"
                isVisible={showToast}
                onClose={() => setShowToast(false)}
            />
        </>
    );
}

export default ArtworkPage;